// import apis and mutations
import {
  PeriodAssignmentResponse,
  PeriodAssignmentStatus,
  queryKeys
} from 'generated/index';
import { mutations, queries } from 'RestAPIs/rapini';

// import components
import { Box, TextField } from '@mui/material';
import { ConfirmationDialog, showSnackbar } from 'components/shared/index';
import { showErrorSnackbar } from 'components/shared/Snackbar';
import AssignmentStatusInformation from '../../flex-portal/Assignments/AssignmentStatusInformation';
import AssignmentMatchDialog from '../../flex-portal/Assignments/AssignmentMatchDialog/AssignmentMatchDialog';
import PeriodAssignmentDialog from 'components/shared/Assignments/PeriodAssignmentDialog';

// import hooks
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

// import utils
import { dateFormatter } from 'components/shared/DataGrid/utils/valueFormatters/dateFormatter';
import TableCRUDLayout from '../CRUD/TableCRUDLayout';

type PendingAction = {
  action: 'return' | 'inprogress';
  id: string;
};

type Props = {
  allowedToProcess?: boolean;
  statuses?: PeriodAssignmentStatus[];
};

const PeriodAssignments = ({ allowedToProcess, statuses }: Props) => {
  const queryClient = useQueryClient();
  const [shouldShowAddEditDialog, setShouldShowAddEditDialog] =
    useState<boolean>(false);
  const [dialogData, setDialogData] = useState<
    PeriodAssignmentResponse | undefined
  >(undefined);
  const [shouldShowConfirmationDialog, setShouldShowConfirmationDialog] =
    useState<boolean>(false);
  const [showMatchDialog, setShowMatchDialog] = useState<boolean>(false);
  const [assignment, setAssignment] = useState<
    PeriodAssignmentResponse | undefined
  >(undefined);
  const [pendingActionForId, setPendingActionForId] =
    useState<PendingAction | null>(null);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const { t } = useTranslation();

  const { data = [], isLoading: isFetching } = queries.useGetPeriodAssignments(
    undefined,
    undefined,
    undefined,
    //@ts-expect-error TODO: BE should accept array of ids
    statuses ? statuses.join(',') : undefined
  );

  const { mutate: inProgressAssignment, isLoading: isLoadingInProgress } =
    mutations.useInProgressPeriodAssignment();
  const { mutate: returnAssignment, isLoading: isLoadingReturn } =
    mutations.useReturnPeriodAssignment();

  const isLoading = isFetching || isLoadingInProgress || isLoadingReturn;
  const gridRows = data.map((r) => ({
    id: r.entityId,
    organizationalUnit: r?.organizationalUnit?.name ?? '',
    description: r.description,
    function: r?.function?.description ?? '',
    hoursPerWeek: r.hoursPerWeek,
    startDate: r.startDate,
    endDate: r.endDate,
    status: r.status ? t(r.status.toLowerCase()) : '',
    employee: r?.employee?.nameInformal ?? '',
    requestedOn: r.requestedOn,
    inProgressOn: r.inProgressOn,
    returnedOn: r.returnedOn,
    completedOn: r.completedOn,
    requestedBy: r.requestedBy,
    inProgressBy: r.inProgressBy,
    returnedBy: r.returnedBy,
    completedBy: r.completedBy,
    reason: r.reason,
    numberOfSubscribedEmployees: r.numberOfSubscribedEmployees
  }));

  const commonOptions = {
    width: 150,
    flex: 1
  };

  const columns = [
    {
      field: 'startDate',
      headerName: t('startDate'),
      valueFormatter: dateFormatter,
      width: 120
    },
    {
      field: 'endDate',
      headerName: t('endDate'),
      valueFormatter: dateFormatter,
      width: 120
    },
    { field: 'hoursPerWeek', headerName: t('hoursPerWeek') },
    {
      field: 'organizationalUnit',
      headerName: t('planUnit'),
      ...commonOptions
    },
    {
      field: 'function',
      headerName: t('function'),
      ...commonOptions
    },
    {
      field: 'description',
      headerName: t('description'),
      ...commonOptions
    },
    {
      field: 'employee',
      headerName: t('employee'),
      ...commonOptions
    },
    {
      field: 'numberOfSubscribedEmployees',
      headerName: t('subscriptions'),
      ...commonOptions
    },
    {
      field: 'status',
      width: 100,
      headerName: t('status'),
      renderCell: ({ row }) => <AssignmentStatusInformation row={row} />
    }
  ];

  const onClickAddRow = () => {
    setShouldShowAddEditDialog(true);
  };

  const handleCloseAddEdit = () => {
    setShouldShowAddEditDialog(false);
    setDialogData(undefined);
  };

  const onSave = () => {
    handleCloseAddEdit();
  };

  const onClickMatch = (rowId: string) => {
    setShowMatchDialog(true);
    const assignment = data.find((a) => a.entityId === rowId);
    setAssignment(assignment);
  };

  const onClickInProgress = (rowId: string) => {
    setShouldShowConfirmationDialog(true);
    setPendingActionForId({
      action: 'inprogress',
      id: rowId
    });
  };

  const onClickReturn = (rowId: string) => {
    setShouldShowConfirmationDialog(true);
    setPendingActionForId({
      action: 'return',
      id: rowId
    });
  };

  const onConfirm = async () => {
    if (pendingActionForId?.action === 'inprogress') {
      await inProgressAssignment(
        {
          periodAssignmentEntityId: pendingActionForId.id
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(queryKeys.getPeriodAssignments.name);
          },
          onError: () => {
            const message = t('somethingWentWrong');
            showSnackbar({
              severity: 'error',
              message: message
            });
          }
        }
      );
    } else if (pendingActionForId?.action === 'return') {
      if (!rejectionReason) {
        showErrorSnackbar(t('flexPortalCapacity.rejectFailedReasonRequired'));
      } else {
        await returnAssignment(
          {
            periodAssignmentEntityId: pendingActionForId.id,
            reason: rejectionReason
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries(
                queryKeys.getPeriodAssignments.name
              );
            },
            onError: () => {
              const message = t('somethingWentWrong');
              showSnackbar({
                severity: 'error',
                message: message
              });
            }
          }
        );
      }

      setRejectionReason('');
    }
    setShouldShowConfirmationDialog(false);
  };

  const onCancelConfirm = () => {
    setRejectionReason('');
    setPendingActionForId(null);
    setShouldShowConfirmationDialog(false);
  };

  return (
    <>
      <Box
        sx={{
          px: '10px',
          pt: '20px'
        }}
      >
        <Box pb={1}>
          <TableCRUDLayout
            rows={gridRows}
            onAddClick={onClickAddRow}
            shouldRenderToolBar={false}
            renderFormContainer={false}
            editable={false}
            addRowDialog={
              <PeriodAssignmentDialog
                isOpen={shouldShowAddEditDialog}
                isFlex={!!allowedToProcess}
                assignment={dialogData}
                handleClose={handleCloseAddEdit}
                onSave={onSave}
              />
            }
            columns={columns}
            initialState={{
              sorting: { sortModel: [{ field: 'startDate', sort: 'asc' }] }
            }}
            customActions={(id, row) => [
              ...(allowedToProcess &&
              row?.status &&
              (row.status === t('requested') || row.status === t('in_progress'))
                ? [
                    {
                      tooltipTitle: t('match'),
                      icon: 'magnifying-glass',
                      onClick: () => onClickMatch(id as string)
                    }
                  ]
                : []),
              ...(allowedToProcess &&
              row?.status &&
              row.status == t('requested')
                ? [
                    {
                      tooltipTitle: t('in_progress'),
                      icon: 'check',
                      onClick: () => onClickInProgress(id as string)
                    }
                  ]
                : []),
              ...(allowedToProcess &&
              row?.status &&
              (row.status === t('requested') || row.status === t('in_progress'))
                ? [
                    {
                      tooltipTitle: t('return'),
                      icon: 'arrow-turn-down-left',
                      onClick: () => onClickReturn(id as string)
                    }
                  ]
                : [])
            ]}
            loading={isLoading}
          />
        </Box>
        {shouldShowConfirmationDialog && pendingActionForId && (
          <ConfirmationDialog
            handleAction={(isConfirmed) => {
              if (isConfirmed) {
                onConfirm();
              } else {
                onCancelConfirm();
              }
            }}
            cancelBtnText={t('no')}
            confirmBtnText={t('yes')}
            title={t('flexPortalCapacity.confirmationTitle')}
            type="warning"
            disableConfirm={
              pendingActionForId?.action === 'return' && !rejectionReason
            }
          >
            <>
              <p>
                {pendingActionForId.action == 'inprogress'
                  ? t('flexPortalCapacity.confirmationBodyInProgress')
                  : t('flexPortalCapacity.confirmationBody', {
                      action: t(
                        `flexPortalCapacity.${pendingActionForId.action}`
                      )
                    })}
              </p>
              {pendingActionForId.action === 'return' && (
                <TextField
                  label={t('rejectionReason')}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  required
                  autoFocus
                />
              )}
            </>
          </ConfirmationDialog>
        )}
      </Box>
      <AssignmentMatchDialog
        open={showMatchDialog}
        assignment={assignment}
        title=""
        handleClose={() => setShowMatchDialog(false)}
      />
    </>
  );
};

export default PeriodAssignments;
